import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '~/redux/hooks';

const BACKSTOP_INTERVAL_MS = 30000;
export const LIVE_INTERVAL_MS = 1000;

/**
 * Remaining life of the admin session, in seconds.
 *
 * Three things this deliberately does not do:
 *
 * 1. It does not decrement a counter. Every tick recomputes from the stored
 *    deadline, because a background tab's timers are throttled to roughly once
 *    a minute after five minutes hidden — and a frozen or discarded tab runs
 *    nothing at all. A decrementing counter would drift by however long the tab
 *    was asleep.
 * 2. It does not derive the deadline from `expiresAt` and `Date.now()`. It
 *    anchors on the server's `expires_in_seconds` plus `performance.now()`,
 *    which is monotonic, so a system clock change or a laptop resuming from
 *    sleep cannot move it.
 * 3. It does not expire the session. Reaching zero is a signal to ask the
 *    server, never a conclusion — see useAdminSessionWatcher.
 *
 * Recomputes on visibilitychange and focus, which is what actually covers the
 * common case of a member returning to a long-hidden tab.
 *
 * Pass `tickMs` of 1000 where the number is on screen and has to visibly
 * count -- the expiry warnings do this. The 30-second default is for
 * consumers that only need to know when a threshold is crossed, and keeps the
 * app-wide watcher from re-rendering every second for nothing.
 *
 * @param {Number} [tickMs] recompute interval
 * @returns {Object} remainingSeconds plus isWarningDue / isExpired flags
 */
const useAdminSessionCountdown = (tickMs: number = BACKSTOP_INTERVAL_MS) => {
    const { status, expiresInSeconds, receivedAtMs, warningSchedule } = useAppSelector(
        ({ adminMfa }) => adminMfa
    );

    // Largest first, so [0] is the outermost rung and the last is the final one.
    const THRESHOLDS = useMemo(
        () => [...(warningSchedule || [])].sort((a, b) => b - a),
        [warningSchedule]
    );

    const FIRST_THRESHOLD = THRESHOLDS[0] || 0;

    const [remainingSeconds, setRemainingSeconds] = useState<number>(expiresInSeconds);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isValid = status === 'valid';

    const computeRemaining = useCallback(() => {
        if (!isValid || receivedAtMs === undefined) return 0;

        const elapsed = (performance.now() - receivedAtMs) / 1000;

        return Math.max(Math.round(expiresInSeconds - elapsed), 0);
    }, [expiresInSeconds, isValid, receivedAtMs]);

    const onRecompute = useCallback(() => {
        setRemainingSeconds(computeRemaining());
    }, [computeRemaining]);

    useEffect(() => {
        const onClear = () => {
            if (intervalRef.current) clearInterval(intervalRef.current);

            intervalRef.current = null;
        };

        onClear();

        if (!isValid) {
            setRemainingSeconds(0);

            return;
        }

        onRecompute();

        intervalRef.current = setInterval(onRecompute, tickMs);

        const onWake = () => onRecompute();

        document.addEventListener('visibilitychange', onWake);
        window.addEventListener('focus', onWake);

        return () => {
            onClear();
            document.removeEventListener('visibilitychange', onWake);
            window.removeEventListener('focus', onWake);
        };
    }, [isValid, onRecompute, tickMs]);

    // The nearest rung still ahead. Changes only when one is crossed, which is
    // what makes the timeout below re-arm once per rung rather than once per
    // deadline — previously only the first warning landed on time and the rest
    // waited for the 30-second backstop.
    const NEXT_THRESHOLD = THRESHOLDS.find(threshold => threshold < remainingSeconds);

    useEffect(() => {
        if (!isValid || NEXT_THRESHOLD === undefined) return;

        const delayMs = (computeRemaining() - NEXT_THRESHOLD) * 1000;

        if (delayMs <= 0) return;

        const timeout = setTimeout(onRecompute, delayMs);

        return () => clearTimeout(timeout);
    }, [computeRemaining, isValid, NEXT_THRESHOLD, onRecompute]);

    /**
     * Recomputed here rather than read from state.
     *
     * remainingSeconds is written by an effect, so in the commit where a new
     * deadline arrives it still holds the previous value — and that value is 0
     * whenever the session had just lapsed, because the effect zeroes it while
     * the status is not 'valid'. Deriving isExpired from it therefore reports
     * "expired" about a deadline the server has only just renewed, for exactly
     * one render.
     *
     * That was survivable while the watcher's response to an expiry was a single
     * request. Once the expiry path also sets the status to 'checking', the
     * status leaves 'valid' and comes back, the state is zeroed again on the way
     * through, and the same false reading returns on every pass — an endless
     * round of admin/access calls.
     */
    const liveRemainingSeconds = computeRemaining();

    return {
        remainingSeconds,
        // Inside the warning band at all — the outermost rung. Which individual
        // rung has been crossed, and whether it has already been shown, is
        // useAdminSessionWatcher's business.
        isWarningDue: isValid && remainingSeconds > 0 && remainingSeconds <= FIRST_THRESHOLD,
        isExpired: isValid && liveRemainingSeconds <= 0,
        thresholds: THRESHOLDS,
        onRecompute
    };
};

export default useAdminSessionCountdown;
